import * as React from 'react'
import { useOutletContext } from 'react-router-dom'

import { BlankSlate, Box } from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'

import PanesHeader from 'modules/core/components/PanesHeader'
import { PanesPad } from 'modules/core/components/PanesPad'
import { useAsyncData, useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import employees from 'modules/employees/store/collections/employees'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import Table from 'modules/surveys/components/Responses/Table'
import SurveyShowHeader from 'modules/surveys/components/Show/SurveyShowHeader'
import SurveyShowPageTabs from 'modules/surveys/components/Show/SurveyShowPageTabs'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import questionGroups, {
  surveyQuestionGroups,
} from 'modules/surveys/store/collections/questionGroups'
import type Form from 'modules/surveys/store/models/Form'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'
import type Survey from 'modules/surveys/store/models/v2/Survey'

export default function ResponsesPage() {
  const { survey, hasSurveyV2, canEditSurvey } = useSurveyShowContext()
  const { form } = useOutletContext<{ form: Form }>()
  const surveyModel = hasSurveyV2 ? (survey as Survey) : form

  const { loading } = useUnsafeAsyncData(
    () =>
      questionGroups.fetch({
        data: {
          surveys_form_id: surveyModel.attributes.id,
        },
      }),
    [surveyModel]
  )

  const { loading: loadingEmployees } = useAsyncData(
    () => employees.fetch({ withCachedFetchParams: true }),
    []
  )

  const answeredQuestionGroups = surveyQuestionGroups(surveyModel).filter((qg) => qg.isAnswered)

  const sortedAnswersQuestionGroups = orderBy(
    answeredQuestionGroups,
    (aqg: QuestionGroup) => aqg.attributes.answered_at,
    ['asc']
  )

  if (!survey) {
    return null
  }

  return (
    <ResponsivePage.Body showHeader={false} loading={loading || loadingEmployees} error={null}>
      <Box gap="s24">
        <SurveyShowHeader survey={survey} canEditSurvey={canEditSurvey} />
        <SurveyShowPageTabs form={survey} canEditSurvey={canEditSurvey} />
      </Box>
      {answeredQuestionGroups.length ? (
        <PanesPad>
          <PanesHeader
            illustration="surveys"
            title={i18n.t('surveys.form.responses.title')}
            description={i18n.t('surveys.form.responses.description')}
          />
          <Table
            form={surveyModel}
            questionGroups={sortedAnswersQuestionGroups}
            employees={employees}
          />
        </PanesPad>
      ) : (
        <BlankSlate
          illustration="surveys"
          title={i18n.t('surveys.form.responses.blank.title')}
          description={i18n.t('surveys.form.responses.blank.description')}
        />
      )}
    </ResponsivePage.Body>
  )
}
